import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../redux/hooks";
import { updateWidgetData, selectPlainSiteContent } from "../redux/slices/siteContentSlice";
import widgetRegistry from "../widgets/widgetRegistry";
import type { FooterData } from "./useFooter";
import type { NavItem } from "../components/navigation";

// Union type for all possible footer widget data types
type FooterWidgetData = FooterData; // Can be extended with specific footer data types

// Widget interface
interface FooterWidget {
  type: string;
  data: FooterWidgetData;
}

// Nav widget interface
interface NavWidget {
  type: string;
  data: {
    items?: NavItem[];
    [key: string]: any;
  };
}

// Hook return interface with logical grouping
interface UseFooterWidgetReturn {
  // Component data
  FooterComponent: React.ComponentType<any> | null;
  footerData: FooterWidgetData | undefined;
  navItems: NavItem[]; // Nav items for dynamic Quick Links
  
  // State
  isEditMode: boolean;
  
  // Loading states
  loading: {
    updateLoading: boolean;
    fetchLoading: boolean;
  };
  
  // Error state
  error: string | null;
  
  // Actions
  actions: {
    update: (data: FooterWidgetData) => void;
  };
}

/**
 * Industry-standard custom hook for managing footer widget functionality
 * Follows enterprise-grade patterns for performance, type safety, and maintainability
 */
export const useFooterWidget = (): UseFooterWidgetReturn => {
  const dispatch = useDispatch();
  
  // Consistent selector usage (useAppSelector)
  const siteContent = useAppSelector(selectPlainSiteContent);
  const { isEditMode } = useAppSelector((state) => state.editMode);
  const { fetchLoading, updateLoading, error: siteContentError } = useAppSelector((state) => state?.siteContent);

  // Performance optimization: Memoize repeated widget finding
  const footerWidget = useMemo((): FooterWidget | undefined => {
    const widgets = siteContent?.widgets ?? [];
    return widgets.find((w: any) => w?.type?.toLowerCase().includes("footer")) as FooterWidget | undefined;
  }, [siteContent?.widgets]);

  // Get nav widget for nav items
  const navWidget = useMemo((): NavWidget | undefined => {
    const widgets = siteContent?.widgets ?? [];
    return widgets.find((w: any) => w?.type?.toLowerCase().includes("nav")) as NavWidget | undefined;
  }, [siteContent?.widgets]);

  // Extract nav items from nav widget
  const navItems = useMemo((): NavItem[] => {
    return navWidget?.data?.items ?? [];
  }, [navWidget]);
  // Memoize component and data extraction
  const { FooterComponent, footerData } = useMemo(() => {
    if (!footerWidget) {
      return { FooterComponent: null, footerData: undefined };
    }

    const { data: footerWidgetData, type: footerType } = footerWidget;
    const FooterComponent = widgetRegistry[footerType as keyof typeof widgetRegistry] || null;

    return { FooterComponent, footerData: footerWidgetData };
  }, [footerWidget]);

  // Error handling: Monitor for update failures
  const combinedError = useMemo(() => {
    return siteContentError ? String(siteContentError) : null;
  }, [siteContentError]);

  /**
   * Footer update handler with error handling
   */
  const handleFooterUpdate = useCallback(
    (newData: FooterWidgetData) => {
      if (isEditMode && footerWidget) {
        try {
          const widgets = siteContent?.widgets ?? [];
          const footerWidgetIndex = widgets.findIndex((w: any) => 
            w?.type?.toLowerCase().includes("footer")
          );
          
          if (footerWidgetIndex !== -1) {
            dispatch(updateWidgetData({ 
              index: footerWidgetIndex, 
              data: newData
            }));
          }
        } catch (error) {
          console.error("Footer update failed:", error);
        }
      }
    },
    [isEditMode, dispatch, footerWidget, siteContent?.widgets]
  );

  // Return with logical grouping and strong typing
  return {
    FooterComponent,
    footerData,
    navItems, // Include nav items for footer widgets
    isEditMode,
    loading: {
      updateLoading,
      fetchLoading
    },
    error: combinedError,
    actions: {
      update: handleFooterUpdate
    }
  };
};
